import React from 'react';
import axios from 'axios';
import { Box, Typography } from '@material-ui/core';
import { Circle, CircleMarker, GeoJSON, MapContainer, Popup, TileLayer } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from './config';

const GERMANY_CENTER = [51.1657, 10.4515];
const GERMANY_ZOOM = 6;

function formatTimestamp(timestamp) {
  return timestamp ? new Date(timestamp).toLocaleString() : 'Unknown';
}

function formatDistance(distance) {
  return `${Math.round(distance)} m`;
}

function formatCoordinates(latitude, longitude) {
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

function formatMetadata(metadata) {
  return JSON.stringify(metadata, null, 2);
}

function featureCollectionWithoutPoints(geo) {
  if(geo?.type !== 'FeatureCollection') {
    return null;
  }

  const features = geo.features.filter((feature) => feature?.geometry?.type !== 'Point');
  return features.length ? {...geo, features} : null;
}

function pointFeatures(entry) {
  if(entry.geo?.type !== 'FeatureCollection') {
    return [];
  }

  return entry.geo.features.filter((feature) => {
    const coordinates = feature?.geometry?.coordinates;
    return feature?.geometry?.type === 'Point'
      && Array.isArray(coordinates)
      && coordinates.length >= 2
      && Number.isFinite(coordinates[0])
      && Number.isFinite(coordinates[1]);
  });
}

export function DeviceLocationsMapScreen() {
  const [locations, setLocations] = React.useState([]);
  const [estimates, setEstimates] = React.useState([]);
  const [assistiveLocations, setAssistiveLocations] = React.useState([]);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isActive = true;

    axios.get(`${API_BASE_URL}/api/v1/device-locations`)
      .then((response) => {
        if(isActive) {
          setLocations(response.data.locations ?? []);
          setEstimates(response.data.estimates ?? []);
          setAssistiveLocations(response.data.assistiveLocations ?? []);
        }
      })
      .catch((requestError) => {
        console.error('Could not load device locations:', requestError);
        if(isActive) {
          setError('Device locations could not be loaded.');
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  function centerOnBrowserLocation(map) {
    if(!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({coords}) => map.setView([coords.latitude, coords.longitude], 11),
      () => {},
      {enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 5000},
    );
  }

  return (
    <Box>
      {error && <Typography color="error" paragraph>{error}</Typography>}
      <MapContainer
        center={GERMANY_CENTER}
        zoom={GERMANY_ZOOM}
        scrollWheelZoom
        style={{height: 'calc(100vh - 64px)', minHeight: 420, width: '100%'}}
        whenCreated={centerOnBrowserLocation}
        attributionControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {estimates.map((estimate) => {
          const reports = locations.filter((location) => location.deviceID === estimate.deviceID);
          const metadata = reports[0]?.metadata;
          const position = [estimate.latitude, estimate.longitude];
          return (
            <React.Fragment key={estimate.deviceID}>
              <Circle center={position} radius={estimate.accuracyM} pathOptions={{color: '#1976d2', fillOpacity: 0.12}} />
              <CircleMarker center={position} radius={7} pathOptions={{color: '#0d47a1', fillColor: '#1976d2', fillOpacity: 1}}>
                <Popup>
                  <strong>{estimate.deviceID}</strong><br />
                  Estimated accuracy: {formatDistance(estimate.accuracyM)}<br />
                  Reports: {estimate.reportCount}<br />
                  {metadata && <>
                    <hr />
                    {metadata.aid !== null && metadata.aid !== undefined && <>AID: {metadata.aid} (0x{metadata.aid.toString(16)})<br /></>}
                    {metadata.name && <>Name: {metadata.name}<br /></>}
                    {metadata.firmwareVersion && <>Firmware: {metadata.firmwareVersion}<br /></>}
                    Class: {metadata.deviceClass}<br />
                    {metadata.calibrated1mRssi !== null && metadata.calibrated1mRssi !== undefined && <>Calibrated RSSI: {metadata.calibrated1mRssi} dBm<br /></>}
                    {metadata.deviceLatitude !== null && metadata.deviceLatitude !== undefined && metadata.deviceLongitude !== null && metadata.deviceLongitude !== undefined && <>Device coordinates: {formatCoordinates(metadata.deviceLatitude, metadata.deviceLongitude)}<br /></>}
                    Sources: {metadata.sources.join(', ')}<br />
                    {metadata.metaInfo && <><hr />Device metadata:<pre>{formatMetadata(metadata.metaInfo)}</pre></>}
                  </>}
                  {reports.map((location, index) => (
                    <div key={`${location.receivedAt}-${index}`}>
                      <hr />
                      GPS accuracy: {formatDistance(location.gpsAccuracyM)}<br />
                      Bluetooth distance: {formatDistance(location.bluetoothDistanceMeanM)} +/- {formatDistance(location.bluetoothDistanceStdDevM)}<br />
                      Bluetooth samples: {location.bluetoothSampleCount}<br />
                      Mean RSSI: {Math.round(location.bluetoothRssiMean)} dBm<br />
                      Captured: {formatTimestamp(location.capturedAt * 1000)}<br />
                      Received: {formatTimestamp(location.receivedAt)}
                    </div>
                  ))}
                </Popup>
              </CircleMarker>
            </React.Fragment>
          );
        })}
        {assistiveLocations.map((entry, entryIndex) => {
          const nonPointGeo = featureCollectionWithoutPoints(entry.geo);
          return (
            <React.Fragment key={`${entry.aid ?? entry.internalName ?? entryIndex}`}>
              {nonPointGeo && <GeoJSON data={nonPointGeo} style={{color: '#ef6c00', weight: 3}} />}
              {pointFeatures(entry).map((feature, featureIndex) => {
                const [longitude, latitude] = feature.geometry.coordinates;
                return (
                  <CircleMarker
                    key={feature.id ?? featureIndex}
                    center={[latitude, longitude]}
                    radius={6}
                    pathOptions={{color: '#e65100', fillColor: '#ff9800', fillOpacity: 1}}
                  >
                    <Popup>
                      <strong><Link to={`/db?edit=${encodeURIComponent(entry.id)}`}>{entry.name ?? entry.internalName ?? 'AssistiveDB device'}</Link></strong><br />
                      {entry.aid !== null && entry.aid !== undefined && <>AID: {entry.aid} (0x{entry.aid.toString(16)})<br /></>}
                      {entry.type && <>Class: {entry.type}<br /></>}
                      AssistiveDB GeoJSON location
                    </Popup>
                  </CircleMarker>
                );
              })}
            </React.Fragment>
          );
        })}
      </MapContainer>
    </Box>
  );
}
